import { Form, Formik } from "formik";
import { useHistory } from "react-router-dom";
import validations from "../../../services/validations";
import EditProfileFields from "../../formsFields/EditProfileFields";
import ProfilePhoneRow from "../../formsFields/ProfilePhoneRow";
import PhoneVerificationPopup from "../../popups/phoneVerificationPopup";
import useProfile from "../../../services/utils/custom hooks/useProfile";
import { useEffect, useState } from "react";
import { useUpdateProfileMutation } from "../../../services/utils/custom hooks/useUpdateProfile";
import AdminContentLayout from "../../../layouts/AdminContentLayout";
import { usePhoneVerificationStatus } from "../../../services/utils/custom hooks/PhoneVerification/usePhoneVerification";
import { normalizeE164 } from "../../../utils/helpers/phoneNumberHelpers";

type ProfileFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  profileImage: string | File;
  npi?: string;
  suffix?: string;
  specialty?: string;
};

function ProfileForm(): JSX.Element {
  const { mutation, updating } = useUpdateProfileMutation();
  const { data: phoneStatus } = usePhoneVerificationStatus();
  const [hydrated, setHydrated] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [profileValues, setProfileValues] = useState<ProfileFormValues>({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    profileImage: "",
  });
  const { data: profile, isLoading } = useProfile();
  const roles = profile?.user?.roles ?? [];
  const history = useHistory();

  useEffect(() => {
    if (isLoading || !profile || hydrated) return;

    const userRoles = profile?.user?.roles ?? [];
    const nextValues: ProfileFormValues = {
      firstName: profile?.user?.firstName || "",
      lastName: profile?.user?.lastName || "",
      email: profile?.user?.email || "",
      role: profile?.roles?.join(", ") || "",
      profileImage:
        profile?.user?.imageUrl || profile?.user?.profileImage || "",
    };

    if (userRoles.includes("Physician")) {
      nextValues.npi = profile.user.npi || "";
      nextValues.suffix = profile.user.suffix || "";
      nextValues.specialty = profile.user.specialty || "";
    }

    setProfileValues(nextValues);
    setHydrated(true);
  }, [isLoading, profile, hydrated]);

  const displayPhone =
    phoneStatus?.mobilePhone ||
    profile?.user?.mobilePhone ||
    profile?.user?.primaryPhone ||
    "";

  const buildUpdatePayload = (values: ProfileFormValues) => {
    const payload: Record<string, unknown> = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      profileImage: values.profileImage,
      mobilePhone: normalizeE164(displayPhone),
    };

    if (roles.includes("Physician")) {
      payload.npi = values.npi;
      payload.suffix = values.suffix;
      payload.specialty = values.specialty;
    }

    return payload;
  };

  const handleSubmit = async (values: ProfileFormValues) => {
    try {
      await mutation.mutateAsync(buildUpdatePayload(values));
      history.push("/access/profile");
    } catch {
      // Toasts handled in mutation
    }
  };

  if (isLoading) {
    return <p>Loading...</p>;
  }

  return (
    <>
      <Formik
        initialValues={profileValues}
        validationSchema={validations.profileFormValidation}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          getFieldMeta,
          submitForm,
          isValid,
        }) => {
          const buttons: any = [
            {
              buttonTheme: "secondary",
              textInformation: {
                text: "CANCEL",
                fontWeight: "font-bold",
              },
              className: "md:mx-5",
              onClick: () => {
                history.push("/access/profile");
              },
            },
            {
              buttonTheme: "primary",
              disabled: !isValid || updating,
              textInformation: {
                text: "Save",
                fontWeight: "font-bold",
              },
              className: "mx-5",
              onClick: () => {
                submitForm();
              },
              loading: updating,
            },
          ];
          return (
            <AdminContentLayout
              pageTitle="Profile | CSTL"
              subHeaderTitle="My Profile"
              showButtonInSubheaderForMobile
              hideSubHeaderSelectors
              customButtons={buttons}
            >
              <Form className="xs:px-4 md:px-4 md:w-[28%] mt-4 overflow-visible">
                <EditProfileFields
                  values={values}
                  touched={touched}
                  showPhysicianFields={roles.includes("Physician")}
                  errors={errors}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  getFieldMeta={getFieldMeta}
                />
                <ProfilePhoneRow
                  phone={displayPhone}
                  verified={!!phoneStatus?.mobilePhoneVerified}
                  onEdit={() => setShowPhoneModal(true)}
                />
              </Form>
            </AdminContentLayout>
          );
        }}
      </Formik>

      {showPhoneModal && (
        <PhoneVerificationPopup
          initialPhone={displayPhone}
          onClose={() => setShowPhoneModal(false)}
          onVerified={() => setShowPhoneModal(false)}
        />
      )}
    </>
  );
}

export default ProfileForm;
